import React from "react";
import { Box, Button, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import moment from "moment";

const stripHtml = (html = "") => {
  const parserContainer = document.createElement("div");
  parserContainer.innerHTML = html;
  return (parserContainer.textContent || parserContainer.innerText || "").trim();
};

const truncate = (value = "", size = 140) => {
  if (!value || value.length <= size) {
    return value;
  }
  return `${value.slice(0, size - 1).trim()}…`;
};

const RelatedArticles = ({ articles = [], allArticlesUrl = "#" }) => {
  if (!articles.length) {
    return null;
  }

  return (
    <Box id="related-articles" sx={{ backgroundColor: "hsl(140 8% 93% / 0.5)", borderTop: "1px solid", borderColor: "divider" }}>
      <Container maxWidth={false} sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 10 } }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 5 }}>
            <Typography component="h2" sx={{ fontSize: { xs: 34, md: 46 }, lineHeight: 1.1, fontWeight: 700, color: "hsl(150 30% 14%)" }}>
              <FormattedMessage id="blog.similar_articles" />
            </Typography>
            <Box component="a" href={allArticlesUrl} sx={{ display: { xs: "none", md: "block" }, fontSize: 14, fontWeight: 600, color: "primary.main", textDecoration: "none" }}>
              <FormattedMessage id="blog.all_articles" /> →
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 3 }}>
          {articles.map((article, index) => {
            const excerpt = truncate(stripHtml(article.content), 130);
            const date = article.updated_at || article.created_at;

            return (
              <Box
                key={article.id}
                component={motion.a}
                href={article.href}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                sx={{
                  display: "block",
                  borderRadius: 3,
                  overflow: "hidden",
                  textDecoration: "none",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  boxShadow: "0 1px 3px hsl(150 30% 14% / 0.06), 0 8px 24px -8px hsl(150 30% 14% / 0.1)",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 12px 40px -12px hsl(150 30% 14% / 0.15)",
                  },
                }}
              >
                <Box sx={{ aspectRatio: "16 / 10", overflow: "hidden" }}>
                  <Box
                    component="img"
                    src={article.cover_picture_url}
                    alt={article.title}
                    sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease", "a:hover &": { transform: "scale(1.05)" } }}
                  />
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Typography sx={{ mb: 1, fontSize: 12, color: "text.secondary" }}>
                    {moment(date).format("DD/MM/YYYY")} · Squarehub
                  </Typography>
                  <Typography sx={{ mb: 1, fontSize: 20, lineHeight: 1.3, fontWeight: 700, color: "hsl(150 30% 14%)" }}>
                    {truncate(article.title, 62)}
                  </Typography>
                  <Typography sx={{ fontSize: 14, color: "text.secondary", lineHeight: 1.45 }}>
                    {excerpt}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 4, textAlign: "center", display: { xs: "block", md: "none" } }}>
          <Button component="a" href={allArticlesUrl} sx={{ textTransform: "none", fontSize: 14, fontWeight: 600, color: "primary.main" }}>
            <FormattedMessage id="blog.all_articles" /> →
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default RelatedArticles;
